import { type RefObject, useCallback, useEffect } from "react";
import { placeModalDialog } from "../lib/ui/modal-dialog";

/**
 * Garde un `<dialog>` modal DANS la partie réellement visible de l'écran —
 * patron DS §6.17, décision géométrique dans `lib/ui/modal-dialog.ts`.
 *
 * Ce que le hook écrit sur l'élément : trois custom properties de géométrie
 * MESURÉE (`--ds-dialog-top`, `--ds-dialog-bottom`, `--ds-dialog-max-h`),
 * consommées par la règle `dialog:modal` de `global.css`. Aucun style de
 * design en ligne : décor, largeur et rayon restent des classes et des jetons.
 * Cette géométrie-là ne peut pas vivre dans une classe — elle dépend de la
 * hauteur du clavier, que seul le navigateur connaît à l'exécution.
 *
 * Les zones sûres sont lues sur le `padding` du `<body>`, qui porte déjà
 * `env(safe-area-inset-*)` (global.css) : une seule source de vérité, et zéro
 * sur le web sans rien de spécial à prévoir.
 *
 * Recalcule à l'ouverture, au redimensionnement et au défilement du viewport
 * VISUEL — les seuls signaux d'apparition du clavier iOS (aucun plugin
 * Capacitor requis), exactement comme `use-anchored-panel`.
 */
export function useModalDialogViewport(
  dialogRef: RefObject<HTMLDialogElement | null>,
  open: boolean,
): void {
  const measure = useCallback(() => {
    const dialog = dialogRef.current;
    // jsdom n'a ni visualViewport ni mise en page : on ne pose rien plutôt
    // qu'une géométrie fausse — la CSS garde alors ses valeurs de repli.
    if (!dialog || typeof window === "undefined") return;

    const visual = window.visualViewport;
    const body = window.getComputedStyle(document.body);
    const placement = placeModalDialog({
      viewport: {
        height: visual?.height ?? window.innerHeight,
        offsetTop: visual?.offsetTop ?? 0,
      },
      layoutHeight: window.innerHeight,
      insetTop: Number.parseFloat(body.paddingTop) || 0,
      insetBottom: Number.parseFloat(body.paddingBottom) || 0,
    });

    dialog.style.setProperty("--ds-dialog-top", `${placement.top}px`);
    dialog.style.setProperty("--ds-dialog-bottom", `${placement.bottom}px`);
    dialog.style.setProperty("--ds-dialog-max-h", `${placement.maxHeight}px`);
  }, [dialogRef]);

  useEffect(() => {
    if (!open) return;
    measure();

    const visual = window.visualViewport;
    window.addEventListener("resize", measure);
    visual?.addEventListener("resize", measure);
    visual?.addEventListener("scroll", measure);
    return () => {
      window.removeEventListener("resize", measure);
      visual?.removeEventListener("resize", measure);
      visual?.removeEventListener("scroll", measure);
    };
  }, [open, measure]);
}
